import React, { useCallback, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import type { Trail } from "@hash/shared";
import { LoginPage } from "./pages/Login";
import { TrailPicker } from "./pages/TrailPicker";
import { CheckInPage } from "./pages/CheckIn";
import { NewTrailPage } from "./pages/NewTrail";
import { HashersPage } from "./pages/Hashers";
import { HasherManagePage } from "./pages/HasherManage";
import { KennelsPage } from "./pages/Kennels";
import { RoleAuditPage } from "./pages/RoleAudit";
import { ReportsPage } from "./pages/Reports";
import { AccountPage } from "./pages/Account";
import { MyHistoryPage } from "./pages/MyHistory";
import { HomePage } from "./pages/Home";
import { api } from "./api/client";
import { resetDemo } from "./api/demo/store";
import { isDemoMode } from "./config";
import { loadHashers } from "./stores/hasherStore";
import { getClaims, isSignedIn, signOut } from "./auth";
import { hareWindowMatches } from "./lib/checkInWindow";
import { localDate } from "./lib/trailDate";
import "./index.css";

type View =
  | { kind: "loading" }
  | { kind: "home"; trails: Trail[] }
  | { kind: "picker"; trails: Trail[] }
  | { kind: "newTrail"; defaultKennelId: string }
  | { kind: "newEvent"; defaultKennelId: string }
  | { kind: "checkin"; trail: Trail; showDetails?: boolean }
  | { kind: "hashers" }
  | { kind: "hasherManage"; hasherId: string }
  | { kind: "kennels" }
  | { kind: "reports" }
  | { kind: "roleAudit" }
  | { kind: "account" }
  | { kind: "history" };

function App() {
  const [signedIn, setSignedIn] = useState(isSignedIn());
  const [view, setView] = useState<View>({ kind: "loading" });
  const [fadeIn, setFadeIn] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const claims = signedIn ? getClaims() : null;
  const isAdmin = claims?.role === "admin";
  // Misman is kennel-scoped; holding any grant opens the roster and kennel
  // screens (roster editing is network-wide for any misman).
  const isStaff = isAdmin || (claims?.mismanKennels?.length ?? 0) > 0;

  useEffect(() => {
    const timer = setTimeout(() => setFadeIn(true), 50);
    return () => clearTimeout(timer);
  }, [signedIn]);

  const loadTrails = useCallback(async () => {
    setView({ kind: "loading" });
    // Fetch 60 days back and 60 days forward — the picker splits the window
    // into its Upcoming (default) and Past tabs.
    const { trails } = await api.trails.list(localDate(-60), localDate(60));

    // Hare landing (Story 8.1): if I'm a hare on event(s) whose check-in window
    // (12h before → 6h after, in the event's time zone) is open right now, deep
    // link to the single match, or surface the matches ordered by proximity.
    const hasherId = getClaims()?.hasherId;
    if (hasherId) {
      const matches = hareWindowMatches(trails, hasherId, Date.now());
      if (matches.length === 1) {
        setView({ kind: "checkin", trail: matches[0] });
        return;
      }
      if (matches.length > 1 && isStaff) {
        setView({ kind: "picker", trails: matches });
        return;
      }
    }

    // Staff land on the full picker; everyone else gets the role-based Home
    // (their profile, today's trail, the trails they're haring, their history).
    // We only deep-link into a check-in when the hare-window rules above apply;
    // matching on "today's date" was too blunt — a misman opening the app on
    // trail day would skip the picker.
    setView(isStaff ? { kind: "picker", trails } : { kind: "home", trails });
  }, [isStaff]);

  // Re-fetch and return to Home without the hare-window deep-link — used by the
  // non-staff menu/back affordances, where an explicit "Home" tap should always
  // show Home even if the hasher is a hare inside an open check-in window.
  const loadHome = useCallback(async () => {
    setView({ kind: "loading" });
    const { trails } = await api.trails.list(localDate(-60), localDate(60));
    setView({ kind: "home", trails });
  }, []);

  useEffect(() => {
    if (!signedIn) return;
    loadTrails().catch((err) => {
      console.error("Failed to load trails", err);
      setView({ kind: "picker", trails: [] });
    });
  }, [signedIn, loadTrails]);

  // Warm the client-side hasher store as soon as we're signed in, so the roster
  // is ready (and searchable locally) by the time any search screen opens.
  useEffect(() => {
    if (!signedIn) return;
    loadHashers().catch((err) => console.error("Failed to load hashers", err));
  }, [signedIn]);

  if (!signedIn || !claims) {
    return (
      <div
        className={`transition-opacity duration-500 ${
          fadeIn ? "opacity-100" : "opacity-0"
        }`}
      >
        <LoginPage
          onSignedIn={() => {
            setSignedIn(true);
            setView({ kind: "loading" });
          }}
        />
      </div>
    );
  }

  const navigate = (next: View) => {
    setMenuOpen(false);
    setView(next);
  };

  return (
    <div
      className={`min-h-screen transition-opacity duration-500 ${
        fadeIn ? "opacity-100" : "opacity-0"
      }`}
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMenuOpen(true)}
            className="-ml-2 w-11 h-11 flex items-center justify-center text-slate-700 hover:text-slate-900"
            aria-label="Open menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <h1 className="text-lg font-bold">Hash Attendance</h1>
          {isDemoMode() && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              Demo
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {(view.kind === "checkin" ||
            view.kind === "newTrail" ||
            view.kind === "newEvent") && (
            <button
              onClick={() => (isStaff ? loadTrails() : loadHome())}
              className="text-sm text-slate-500 hover:text-slate-900"
              aria-label={isStaff ? "Back to trails" : "Back to home"}
            >
              ← {isStaff ? "Trails" : "Home"}
            </button>
          )}
          <button
            onClick={() => {
              signOut();
              setSignedIn(false);
            }}
            className="text-sm text-slate-500 hover:text-slate-900"
          >
            Sign out
          </button>
        </div>
      </header>

      {menuOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <nav
            className="fixed inset-y-0 left-0 z-50 w-72 max-w-[80%] bg-white shadow-xl flex flex-col"
            aria-label="Main menu"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <span className="text-lg font-bold">Menu</span>
              <button
                onClick={() => setMenuOpen(false)}
                className="-mr-2 w-11 h-11 flex items-center justify-center text-slate-500 hover:text-slate-900"
                aria-label="Close menu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              </button>
            </div>
            <ul className="flex-1 py-2">
              <li>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    const load = isStaff ? loadTrails : loadHome;
                    load().catch((err) => {
                      console.error("Failed to load trails", err);
                      setView(
                        isStaff
                          ? { kind: "picker", trails: [] }
                          : { kind: "home", trails: [] }
                      );
                    });
                  }}
                  className="w-full text-left px-4 py-3 min-h-[44px] text-slate-900 active:bg-slate-100 hover:bg-slate-50"
                >
                  {isStaff ? "Trails" : "Home"}
                </button>
              </li>
              {isStaff && (
                <>
                  <li>
                    <button
                      onClick={() => navigate({ kind: "hashers" })}
                      className="w-full text-left px-4 py-3 min-h-[44px] text-slate-900 active:bg-slate-100 hover:bg-slate-50"
                    >
                      Hashers
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => navigate({ kind: "kennels" })}
                      className="w-full text-left px-4 py-3 min-h-[44px] text-slate-900 active:bg-slate-100 hover:bg-slate-50"
                    >
                      Kennels
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => navigate({ kind: "reports" })}
                      className="w-full text-left px-4 py-3 min-h-[44px] text-slate-900 active:bg-slate-100 hover:bg-slate-50"
                    >
                      Reports
                    </button>
                  </li>
                </>
              )}
              {isAdmin && (
                <li>
                  <button
                    onClick={() => navigate({ kind: "roleAudit" })}
                    className="w-full text-left px-4 py-3 min-h-[44px] text-slate-900 active:bg-slate-100 hover:bg-slate-50"
                  >
                    Role changes
                  </button>
                </li>
              )}
              <li>
                <button
                  onClick={() => navigate({ kind: "history" })}
                  className="w-full text-left px-4 py-3 min-h-[44px] text-slate-900 active:bg-slate-100 hover:bg-slate-50"
                >
                  My history
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate({ kind: "account" })}
                  className="w-full text-left px-4 py-3 min-h-[44px] text-slate-900 active:bg-slate-100 hover:bg-slate-50"
                >
                  Account
                </button>
              </li>
              {isDemoMode() && (
                <li>
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          "Reset the demo to its starting data? This clears any changes you've made in this browser."
                        )
                      ) {
                        resetDemo();
                        window.location.reload();
                      }
                    }}
                    className="w-full text-left px-4 py-3 min-h-[44px] text-amber-700 active:bg-amber-50 hover:bg-amber-50"
                  >
                    Reset demo data
                  </button>
                </li>
              )}
            </ul>
          </nav>
        </>
      )}

      <main>
        {view.kind === "loading" && (
          <p className="px-4 py-6 text-sm text-slate-500">Loading…</p>
        )}
        {view.kind === "home" && (
          <HomePage
            trails={view.trails}
            hasherId={claims.hasherId}
            onManageTrail={(t) => setView({ kind: "checkin", trail: t })}
            onEditTrailDetails={(t) =>
              setView({ kind: "checkin", trail: t, showDetails: true })
            }
            onEditProfile={() => setView({ kind: "account" })}
            onViewHistory={() => setView({ kind: "history" })}
          />
        )}
        {view.kind === "picker" && (
          <TrailPicker
            trails={view.trails}
            isAdmin={isAdmin}
            onSelectTrail={(t) => setView({ kind: "checkin", trail: t })}
            onCreateTrail={() =>
              setView({
                kind: "newTrail",
                // Most-recent trail's kennelId is the best guess for what the
                // organizer is about to pick — pre-fill, they can change it.
                defaultKennelId: view.trails[0]?.kennelId ?? "",
              })
            }
            onCreateEvent={() =>
              setView({
                kind: "newEvent",
                defaultKennelId: view.trails[0]?.kennelId ?? "",
              })
            }
            onTrailDeleted={(id, renumbered) => {
              // Renumbering shifted the surviving trails' numbers and ids, so
              // local state is stale — refetch. Otherwise just drop the row.
              if (renumbered > 0) {
                loadTrails().catch((err) =>
                  console.error("Failed to reload trails", err)
                );
                return;
              }
              setView({
                kind: "picker",
                trails: view.trails.filter((t) => t.id !== id),
              });
            }}
          />
        )}
        {view.kind === "newTrail" && (
          <NewTrailPage
            defaultKennelId={view.defaultKennelId}
            onCreated={(t) => setView({ kind: "checkin", trail: t })}
            onCancel={() => loadTrails()}
          />
        )}
        {view.kind === "newEvent" && (
          <NewTrailPage
            mode="special"
            defaultKennelId={view.defaultKennelId}
            onCreated={(t) => setView({ kind: "checkin", trail: t })}
            onCancel={() => loadTrails()}
          />
        )}
        {view.kind === "checkin" && (
          <CheckInPage
            trail={view.trail}
            initialShowDetails={view.showDetails}
          />
        )}
        {view.kind === "hashers" && (
          <HashersPage
            onOpenHasher={(hasherId) =>
              setView({ kind: "hasherManage", hasherId })
            }
          />
        )}
        {view.kind === "hasherManage" && (
          <HasherManagePage
            hasherId={view.hasherId}
            onBack={() => setView({ kind: "hashers" })}
          />
        )}
        {view.kind === "kennels" && <KennelsPage isAdmin={isAdmin} />}
        {view.kind === "reports" && <ReportsPage />}
        {view.kind === "roleAudit" && <RoleAuditPage />}
        {view.kind === "account" && <AccountPage phone={claims.phone} />}
        {view.kind === "history" && <MyHistoryPage />}
      </main>
    </div>
  );
}

// In dev builds only, optionally start the MSW browser worker so Playwright
// e2e specs drive the SPA against the same handlers as the Vitest suite. The
// `import.meta.env.DEV` guard is statically false in production, so the dynamic
// import (and all of MSW) is dead-code-eliminated from the prod bundle.
async function enableMocking(): Promise<void> {
  if (!import.meta.env.DEV || import.meta.env.VITE_MSW !== "true") return;
  const { worker } = await import("../test/msw/browser");
  await worker.start({ onUnhandledRequest: "error" });
}

enableMocking().then(() => {
  createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
});
